import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
import type { CSSProperties, ReactElement, RefObject } from 'react';
import { Outlet, useNavigate, useParams } from 'react-router';

import { AppStyles, withHeight } from '@/config/appStyles';
import { LanguageRoute } from '@/config/router/routes';
import { chatRepository, ChatRepositoryApi } from '@/data/chatRepositoryApi';
import { personRepository } from '@/data/personRepositoryImpl';
import { appLanguageFromString, type AppLanguage } from '@/domain/models/appLanguage';
import { ChatEvent } from '@/domain/models/chatEvent';
import type { ConfigModel } from '@/domain/models/configModel';
import type { PersonModel } from '@/domain/models/personModel';
import type { PersonQuestionModel } from '@/domain/models/personQuestionModel';
import type { L10n } from '@/l10n/types';
import { useL10n } from '@/l10n/useL10n';
import { ChatController } from '@/presentation/providers/chatController/chatController';
import {
  ChatControllerProvider,
  useChatController,
} from '@/presentation/providers/chatController/chatControllerContext';
import type { ChatValue } from '@/presentation/providers/chatController/chatValue';
import { appStateStore, useAppState } from '@/presentation/stores/appStateStore';
import { AnimatedSwitcher } from '@/presentation/widgets/AnimatedSwitcher';
import { BackgroundWrapper } from '@/presentation/widgets/BackgroundWrapper';
import { IgnoreAnimatedSwitcher } from '@/presentation/widgets/IgnoreAnimatedSwitcher';
import { PrimaryShaderGradient } from '@/presentation/widgets/PrimaryShaderGradient';
import { calculateLineCount } from '@/utils/sizeUtil';
import { useScreen } from '@/utils/useScreen';
import { useValueNotifier } from '@/utils/useValueNotifier';

import { ChatActions } from './ChatActions';
import { ChatActionType } from './chatActionType';
import { ChatKeyboardMode } from './ChatKeyboardMode';
import { ChatView } from './ChatView';
import { PersonQuestionsGrid } from './PersonQuestionsGrid';
import { useAnimatedSize } from './useAnimatedSize';
import styles from './ChatPage.module.css';

/**
 * Перенос `chat_page/chat_page.dart` — главный экран приложения.
 *
 * Контроллер чата живёт ровно столько, сколько экран: создаётся при первом
 * рендере и закрывается при размонтировании. Поэтому корень приложения не
 * должен оборачиваться в `React.StrictMode`: он монтирует дерево дважды,
 * первый контроллер при этом закрывается вместе со своими SSE-потоками.
 *
 * `headerActions` в Dart передавались пустым списком — весь блок с обратным
 * отсчётом там закомментирован, переносить нечего.
 */

/** `AppStyles.s36w600.copyWith(height: 36 / 36)` */
const DESCRIPTION_STYLE = withHeight(AppStyles.s36w600, 36 / 36);

/** `AppStyles.s64w600` + `AutoSizeText(minFontSize: 24)`. */
const TITLE_MAX_FONT_SIZE = 64;
const TITLE_MIN_FONT_SIZE = 24;

/** `AnimatedSwitcher(duration: Duration(milliseconds: 200))` у заголовка. */
const TITLE_SWITCH_DURATION_MS = 200;

/** `IgnoreAnimatedSwitcher(duration: Duration(milliseconds: 400))`. */
const BRANCH_SWITCH_DURATION_MS = 400;

/** `AnimatedSize(duration: Duration(milliseconds: 400))` у блока ответа. */
const ANSWER_SIZE_DURATION_MS = 400;

/** `PrimaryShaderGradient(enable: descriptionLength >= 3)`. */
const GRADIENT_LINE_THRESHOLD = 3;

/** Параметры маршрута `/select-language/:lang/chat/:personId`. */
interface ChatRouteParams {
  readonly language: AppLanguage;
  readonly personId: number;
}

/**
 * Разбор параметров адреса. В Dart их приводил сгенерированный `ChatRoute`
 * (`AppLanguage.fromString(lang)` и `int personId`); здесь то же самое,
 * с той же реакцией на мусор — исключением.
 */
function useChatRouteParams(): ChatRouteParams {
  const { lang, personId } = useParams();
  const parsedId = Number.parseInt(personId ?? '', 10);
  if (!Number.isInteger(parsedId)) {
    throw new Error(`ChatPage: в адресе нет числового personId ("${personId}")`);
  }
  return { language: appLanguageFromString(lang ?? ''), personId: parsedId };
}

/** `config.persons.firstWhere((element) => element.id == widget.personId)`. */
function findPerson(config: ConfigModel | null, personId: number): PersonModel {
  const person = config?.persons.find((element) => element.id === personId);
  if (person === undefined) {
    throw new Error(`ChatPage: в конфигурации нет собеседника с id ${personId}`);
  }
  return person;
}

/**
 * `AutoSizeText(minFontSize: 24)`.
 *
 * Во Flutter виджет сам подбирал кегль под коробку. В вебе кегль ищется
 * двоичным поиском с шагом 1 — так же, как это делает сам AutoSizeText, —
 * и замер идёт в `useLayoutEffect`, то есть до первой отрисовки кадра.
 */
function useAutoSizeFontSize(
  boxRef: RefObject<HTMLElement | null>,
  textRef: RefObject<HTMLElement | null>,
  text: string,
): number {
  const [fontSize, setFontSize] = useState(TITLE_MAX_FONT_SIZE);
  const { width, height } = useScreen();

  useLayoutEffect(() => {
    const box = boxRef.current;
    const node = textRef.current;
    if (box === null || node === null) return;

    const fits = (size: number): boolean => {
      node.style.setProperty('font-size', `${size}px`);
      return (
        node.scrollHeight <= box.clientHeight && node.scrollWidth <= box.clientWidth
      );
    };

    let low = TITLE_MIN_FONT_SIZE;
    let high = TITLE_MAX_FONT_SIZE;
    let best = TITLE_MIN_FONT_SIZE;
    while (low <= high) {
      const middle = Math.floor((low + high) / 2);
      if (fits(middle)) {
        best = middle;
        low = middle + 1;
      } else {
        high = middle - 1;
      }
    }
    node.style.removeProperty('font-size');
    setFontSize(best);
  }, [boxRef, textRef, text, width, height]);

  return fontSize;
}

interface ChatTitleProps {
  readonly title: string;
}

function ChatTitle({ title }: ChatTitleProps): ReactElement {
  const boxRef = useRef<HTMLDivElement | null>(null);
  const textRef = useRef<HTMLSpanElement | null>(null);
  const fontSize = useAutoSizeFontSize(boxRef, textRef, title);
  return (
    <div ref={boxRef} className={styles.titleFit}>
      <span
        ref={textRef}
        className={styles.titleText}
        style={{ '--title-font-size': `${fontSize}px` } as CSSProperties}
      >
        {title}
      </span>
    </div>
  );
}

interface InitialChatLayoutProps {
  readonly title: string;
  readonly showQuestions: boolean;
  readonly description: string;
  readonly gradientEnabled: boolean;
  readonly personId: number;
  readonly onQuestionSelect: (question: PersonQuestionModel) => void;
}

/** `KeyedSubtree(key: ValueKey('initial chat'), …)` — экран до начала диалога. */
function InitialChatLayout(props: InitialChatLayoutProps): ReactElement {
  const { title, showQuestions, description, gradientEnabled } = props;
  const { outerRef, innerRef } = useAnimatedSize({
    durationMs: ANSWER_SIZE_DURATION_MS,
  });
  return (
    <div className={styles.initial}>
      <div className={styles.titlePadding}>
        <div className={styles.titleBox}>
          <AnimatedSwitcher
            switchKey={title}
            duration={TITLE_SWITCH_DURATION_MS}
            className={styles.titleSwitcher}
          >
            <ChatTitle title={title} />
          </AnimatedSwitcher>
        </div>
      </div>
      <PrimaryShaderGradient enable={gradientEnabled} className={styles.flexible}>
        <div className={styles.scroll}>
          {showQuestions ? (
            <PersonQuestionsGrid
              personId={props.personId}
              onQuestionSelect={props.onQuestionSelect}
            />
          ) : (
            <div className={styles.answerBox}>
              <div ref={outerRef} className={styles.answerSize}>
                <div ref={innerRef} className={styles.answerPadding}>
                  <span className={styles.answer}>{description}</span>
                </div>
              </div>
            </div>
          )}
        </div>
      </PrimaryShaderGradient>
    </div>
  );
}

/** Лестница заголовков из Dart — порядок условий важен. */
function titleOf(chatValue: ChatValue, l10n: L10n): string {
  if (chatValue.personAnimating) return l10n.hear_you_coming;
  if (chatValue.isRecording) return l10n.speek;
  const question = chatValue.firstQuestion?.message ?? '';
  return question.length === 0 ? l10n.hello_lets_speek : question;
}

interface ChatActionPair {
  readonly left: ChatActionType | null;
  readonly right: ChatActionType | null;
}

/** Выбор кнопок по бокам от микрофона — условия из `ChatPage.build`. */
function actionsOf(chatValue: ChatValue): ChatActionPair {
  const canShowKeyboard = !chatValue.busy && !chatValue.keyboardMode;
  const showChat = chatValue.showChat;
  const selectLeft = (): ChatActionType | null => {
    if (!showChat) return ChatActionType.selectLanguage;
    return chatValue.busy ? null : ChatActionType.backButton;
  };
  const selectRight = (): ChatActionType | null => {
    if (canShowKeyboard) return ChatActionType.keyboardType;
    return chatValue.fetchingResponse ? ChatActionType.closeConnection : null;
  };
  return { left: canShowKeyboard ? selectLeft() : null, right: selectRight() };
}

/**
 * `ChatRepositoryApi.transcriptionUrl(person: …, config: …) != null` —
 * у аватара настроено распознавание речи.
 */
function voiceInputEnabledFor(
  person: PersonModel,
  config: ConfigModel | null,
): boolean {
  return ChatRepositoryApi.transcriptionUrl({ person, config }) !== null;
}

/** `ValueKey('chat' | 'keyboard mode' | 'initial chat')` у свитчера. */
function branchKeyOf(chatValue: ChatValue): string {
  if (chatValue.showChat) return 'chat';
  return chatValue.keyboardMode ? 'keyboard mode' : 'initial chat';
}

interface ChatBranchProps {
  readonly chatValue: ChatValue;
  readonly controller: ChatController;
  readonly l10n: L10n;
  readonly descriptionLength: number;
  readonly selectedMessage: number | null;
  readonly onSelectMessage: (id: number | null) => void;
}

/** Ребёнок свитчера: лента, клавиатурный режим или начальный экран. */
function ChatBranch(props: ChatBranchProps): ReactElement {
  const { chatValue, controller, l10n, descriptionLength } = props;
  if (chatValue.showChat) {
    return (
      <ChatView
        chatValue={chatValue}
        selectedMessage={props.selectedMessage}
        onSelectMessage={props.onSelectMessage}
      />
    );
  }
  if (chatValue.keyboardMode) {
    return (
      <ChatKeyboardMode
        onCancel={() => controller.disableKeyboardMode()}
        onComplete={(value) => {
          controller.disableKeyboardMode();
          if (value.trim().length > 0) void controller.addTextMessage(value);
        }}
      />
    );
  }
  const question = chatValue.firstQuestion?.message ?? '';
  return (
    <InitialChatLayout
      title={titleOf(chatValue, l10n)}
      showQuestions={
        !chatValue.personAnimating &&
        !chatValue.isRecording &&
        !chatValue.busy &&
        question.length === 0
      }
      description={chatValue.firstAnswer?.message ?? ''}
      gradientEnabled={descriptionLength >= GRADIENT_LINE_THRESHOLD}
      personId={chatValue.person.id}
      onQuestionSelect={(item) => void controller.addTextMessage(item.question)}
    />
  );
}

function ChatScreen(): ReactElement {
  const controller = useChatController();
  const chatValue = useValueNotifier(controller);
  const appState = useAppState();
  const l10n = useL10n();
  // `LayoutBuilder` в теле Scaffold даёт ровно размер окна.
  const { width } = useScreen();
  const [selectedMessage, setSelectedMessage] = useState<number | null>(null);

  const descriptionLength = calculateLineCount(
    chatValue.firstAnswer?.message ?? '',
    DESCRIPTION_STYLE,
    width,
  );
  const actions = actionsOf(chatValue);

  return (
    <BackgroundWrapper>
      <div className={styles.column}>
        <div className={styles.expanded}>
          <IgnoreAnimatedSwitcher
            switchKey={branchKeyOf(chatValue)}
            duration={BRANCH_SWITCH_DURATION_MS}
            className={styles.branchSwitcher}
          >
            <ChatBranch
              chatValue={chatValue}
              controller={controller}
              l10n={l10n}
              descriptionLength={descriptionLength}
              selectedMessage={selectedMessage}
              onSelectMessage={setSelectedMessage}
            />
          </IgnoreAnimatedSwitcher>
        </div>
        {!chatValue.keyboardMode && (
          <ChatActions
            chatMode={chatValue.showChat}
            chatValue={chatValue}
            voiceInputEnabled={voiceInputEnabledFor(
              chatValue.person,
              appState.config,
            )}
            leftAction={actions.left}
            rightAction={actions.right}
          />
        )}
      </div>
    </BackgroundWrapper>
  );
}

/**
 * `initState` + `dispose` из `_ChatPageState`: контроллер создаётся один
 * раз и закрывается вместе с экраном.
 *
 * `createPerson` — функция, а не готовый объект: в Dart собеседник
 * вычитывался из состояния один раз, в `initState` (`ref.read`), и
 * последующая перезагрузка конфигурации экран уже не трогала.
 */
function useOwnedChatController(
  createPerson: () => PersonModel,
  language: AppLanguage,
): ChatController {
  const navigate = useNavigate();
  // `if (mounted)` из `_returnToLanguage`.
  const mountedRef = useRef(true);
  // Ссылка на колбэк обязана быть неизменной: её смена пересоздала бы
  // контроллер вместе со всеми его соединениями.
  const navigateRef = useRef(navigate);
  useEffect(() => {
    navigateRef.current = navigate;
  });
  const returnToLanguage = useCallback((): void => {
    if (!mountedRef.current) return;
    LanguageRoute.go(navigateRef.current);
  }, []);

  const controllerRef = useRef<ChatController | null>(null);
  if (controllerRef.current === null) {
    controllerRef.current = new ChatController({
      initialPerson: createPerson(),
      initialLanguage: language,
      chatRepository,
      onTimeExceed: returnToLanguage,
      personRepository,
    });
  }
  const controller = controllerRef.current;

  useEffect(() => {
    const onChatEvent = (event: ChatEvent): void => {
      switch (event) {
        case ChatEvent.complete:
          returnToLanguage();
          break;
      }
    };
    controller.addEventListener(onChatEvent);
    return () => {
      mountedRef.current = false;
      controller.removeEventListener(onChatEvent);
      controller.dispose();
    };
  }, [controller, returnToLanguage]);

  return controller;
}

export function ChatPage(): ReactElement {
  const { language, personId } = useChatRouteParams();
  const controller = useOwnedChatController(
    // `ref.read(appStateProvider).config!` — снимок на момент открытия.
    () => findPerson(appStateStore.getState().appState.config, personId),
    language,
  );

  return (
    <div className={styles.root}>
      <ChatControllerProvider controller={controller}>
        {/* TapRegion(onTapInside: …): любое касание экрана продлевает сеанс. */}
        <div
          className={styles.body}
          onPointerDown={() => controller.chatDurationController.restartTimer()}
        >
          <ChatScreen />
        </div>
        {/* AiMessageSourceRoute — прозрачная страница поверх чата. */}
        <Outlet />
      </ChatControllerProvider>
    </div>
  );
}
